// client/src/components/NetWorth/NetWorthPage.js
import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  Alert,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { NetWorthProvider, useNetWorth } from '../../contexts/NetWorthContext';

// ─────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────
const LIABILITY_TYPES = ['loan', 'mortgage', 'creditcard', 'credit'];

const TYPE_LABELS = {
  checking:   'Checking',
  savings:    'Savings',
  ewallet:    'E-Wallet',
  cash:       'Cash',
  credit:     'Credit',
  investment: 'Investment',
  loan:       'Loan',
  mortgage:   'Mortgage',
  creditcard: 'Credit Card',
};

const TYPE_COLORS = {
  checking:   '#3b82f6',
  savings:    '#10b981',
  ewallet:    '#8b5cf6',
  cash:       '#f59e0b',
  credit:     '#ef4444',
  investment: '#06b6d4',
  loan:       '#f97316',
  mortgage:   '#dc2626',
  creditcard: '#be185d',
};

const TYPE_ICONS = {
  checking:   '🏦',
  savings:    '💰',
  ewallet:    '📱',
  cash:       '💵',
  credit:     '💳',
  investment: '📈',
  loan:       '🏷️',
  mortgage:   '🏠',
  creditcard: '💳',
};

const formatCurrency = (value, currency = 'EUR') => {
  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
};

// ─────────────────────────────────────────────────────
// MetricCard
// ─────────────────────────────────────────────────────
const MetricCard = ({ label, value, currency, color, icon: Icon, subtitle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        flex: 1,
        border: `1px solid ${alpha(color, 0.3)}`,
        bgcolor: isDark ? alpha(color, 0.08) : alpha(color, 0.04),
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: 2,
              bgcolor: alpha(color, 0.15),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color, fontSize: 22 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700} color={color} sx={{ mt: 0.5 }}>
          {formatCurrency(value, currency)}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────
// TrendChart
// ─────────────────────────────────────────────────────
const TrendChart = ({ trend }) => {
  const theme = useTheme();

  const data = useMemo(() =>
    trend.map(snap => ({
      month: snap.month,
      Assets: parseFloat(snap.totalAssets.toFixed(2)),
      Liabilities: parseFloat(snap.totalLiabilities.toFixed(2)),
      'Net Worth': parseFloat(snap.netWorth.toFixed(2)),
    })),
    [trend]
  );

  if (!data.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          No historical data yet. Snapshots are taken monthly.
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="gradAssets" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#10b981" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="gradLiabilities" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.35} />
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="gradNetWorth" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
          width={55}
        />
        <RechartsTooltip
          contentStyle={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value) => [formatCurrency(value), '']}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
          iconType="circle"
          iconSize={10}
        />
        <Area
          type="monotone"
          dataKey="Assets"
          stroke="#10b981"
          strokeWidth={2}
          fill="url(#gradAssets)"
          dot={false}
        />
        <Area
          type="monotone"
          dataKey="Liabilities"
          stroke="#ef4444"
          strokeWidth={2}
          fill="url(#gradLiabilities)"
          dot={false}
        />
        <Area
          type="monotone"
          dataKey="Net Worth"
          stroke="#3b82f6"
          strokeWidth={2.5}
          fill="url(#gradNetWorth)"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ─────────────────────────────────────────────────────
// AccountRow
// ─────────────────────────────────────────────────────
const AccountRow = ({ account }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const color = TYPE_COLORS[account.type] || '#3b82f6';

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 1.25,
        px: 1.5,
        borderRadius: 2,
        '&:hover': { bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03) },
        transition: 'background 0.15s',
      }}
    >
      {/* Left: icon + name + type badge */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <Typography fontSize={22} lineHeight={1} sx={{ flexShrink: 0 }}>
          {TYPE_ICONS[account.type] || '🏦'}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {account.name}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Chip
              label={TYPE_LABELS[account.type] || account.type}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: alpha(color, isDark ? 0.25 : 0.12),
                color: color,
              }}
            />
            {account.interestRate != null && (
              <Typography variant="caption" color="text.secondary">
                {account.interestRate}% APR
              </Typography>
            )}
            {account.minimumPayment != null && (
              <Typography variant="caption" color="text.secondary">
                · min {formatCurrency(account.minimumPayment)}/mo
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Right: balance */}
      <Typography
        variant="body2"
        fontWeight={700}
        color={account.isLiability ? 'error.main' : 'success.main'}
        sx={{ flexShrink: 0, ml: 1 }}
      >
        {account.isLiability ? '−' : '+'}{formatCurrency(Math.abs(account.balance), account.currency)}
      </Typography>
    </Stack>
  );
};

// ─────────────────────────────────────────────────────
// BreakdownSection
// ─────────────────────────────────────────────────────
const BreakdownSection = ({ title, accounts, color, emptyMsg }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="subtitle2" fontWeight={700} color={color}>
          {title}
        </Typography>
        <Chip
          label={accounts.length}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: isDark ? alpha(color, 0.2) : alpha(color, 0.1),
            color,
          }}
        />
      </Stack>

      {accounts.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 2.5, py: 1 }}>
          {emptyMsg}
        </Typography>
      ) : (
        <Box>
          {accounts.map((acc, i) => (
            <React.Fragment key={acc.accountId || i}>
              {i > 0 && <Divider sx={{ mx: 1.5, opacity: 0.5 }} />}
              <AccountRow account={acc} />
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────
// NetWorthPageInner  (consumes context)
// ─────────────────────────────────────────────────────
const NetWorthPageInner = () => {
  const theme = useTheme();
  const { netWorthData, loading, error, refresh } = useNetWorth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button size="small" onClick={refresh}>Retry</Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!netWorthData) return null;

  const { current, trend } = netWorthData;
  const { totalAssets = 0, totalLiabilities = 0, netWorth = 0, breakdown = [] } = current || {};

  const assets      = breakdown.filter(a => !a.isLiability);
  const liabilities = breakdown.filter(a =>  a.isLiability);

  const netWorthColor = netWorth >= 0 ? '#10b981' : '#ef4444';

  const hasAccounts = breakdown.length > 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      {/* ── Page header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Net Worth
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assets minus liabilities across all your accounts
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={refresh}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Stack>

      {!hasAccounts ? (
        /* ── Empty state ── */
        <Card elevation={1} sx={{ borderRadius: 3, textAlign: 'center', py: 8 }}>
          <AccountBalanceIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No accounts yet
          </Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>
            Add your first account to start tracking your net worth.
          </Typography>
        </Card>
      ) : (
        <>
          {/* ── Hero metric cards ── */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            mb={3}
          >
            <MetricCard
              label="Total Assets"
              value={totalAssets}
              color="#10b981"
              icon={TrendingUpIcon}
              subtitle={`${assets.length} asset account${assets.length !== 1 ? 's' : ''}`}
            />
            <MetricCard
              label="Net Worth"
              value={netWorth}
              color={netWorthColor}
              icon={netWorth >= 0 ? TrendingUpIcon : TrendingDownIcon}
              subtitle={netWorth >= 0 ? 'Assets exceed liabilities' : 'Liabilities exceed assets'}
            />
            <MetricCard
              label="Total Liabilities"
              value={totalLiabilities}
              color="#ef4444"
              icon={TrendingDownIcon}
              subtitle={`${liabilities.length} liability account${liabilities.length !== 1 ? 's' : ''}`}
            />
          </Stack>

          {/* ── Trend chart ── */}
          <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                12-Month Trend
              </Typography>
              <TrendChart trend={trend || []} />
            </CardContent>
          </Card>

          {/* ── Account breakdown table ── */}
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Account Breakdown
              </Typography>

              <BreakdownSection
                title="Assets"
                accounts={assets}
                color="#10b981"
                emptyMsg="No asset accounts found."
              />

              {liabilities.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <BreakdownSection
                    title="Liabilities"
                    accounts={liabilities}
                    color="#ef4444"
                    emptyMsg="No liability accounts found."
                  />
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────
// NetWorthPage  (exported — wraps with its own provider)
// ─────────────────────────────────────────────────────
const NetWorthPage = () => (
  <NetWorthProvider>
    <NetWorthPageInner />
  </NetWorthProvider>
);

export default NetWorthPage;
